import { msalInstance, outlookScopes } from './outlookUtils';

// Extended scopes for Teams meetings (includes OnlineMeetings.ReadWrite)
export const teamsScopes = [
  ...outlookScopes,
  "OnlineMeetings.ReadWrite"
];

/**
 * Create a Microsoft Teams meeting
 * @param {Object} meetingData - Meeting data including title, date, time, etc.
 * @returns {Promise<Object>} Meeting details with Teams link
 */
export async function createTeamsMeeting(meetingData) {
  try {
    // ✅ Validate meetingData structure before token acquisition
    if (!meetingData || typeof meetingData !== 'object') {
      throw new Error('Invalid meeting data provided - must be an object');
    }

    if (!meetingData.title || typeof meetingData.title !== 'string') {
      throw new Error('Meeting title is required and must be a string');
    }

    if (!meetingData.date || typeof meetingData.date !== 'string') {
      throw new Error('Meeting date is required and must be a string');
    }

    console.log('Creating Teams meeting with data:', meetingData);
    
    // Get current account
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      throw new Error('No authenticated Microsoft account found. Please connect to Outlook first.');
    }

    // ✅ Add try/catch around all Microsoft Graph API fetches
    let tokenResponse;
    try {
      // Get access token with Teams permissions
      const tokenRequest = {
        account: accounts[0],
        scopes: teamsScopes,
        forceRefresh: false
      };

      tokenResponse = await msalInstance.acquireTokenSilent(tokenRequest);
    } catch (tokenError) {
      console.error('Error acquiring Teams token:', tokenError);
      throw new Error(`Failed to get Teams access token: ${tokenError.message || 'Unknown token error'}`);
    }

    const accessToken = tokenResponse.accessToken;

    // Format the meeting data for Teams
    const teamsEventData = formatMeetingForTeams(meetingData);
    
    // Create calendar event with Teams meeting
    const calendarEvent = await createCalendarEventWithTeams(accessToken, teamsEventData);
    
    return {
      success: true,
      meetingLink: calendarEvent.onlineMeeting?.joinUrl || null,
      eventId: calendarEvent.id,
      webLink: calendarEvent.webLink,
      details: calendarEvent
    };
  } catch (error) {
    console.error('Error creating Teams meeting:', error);
    
    if (error.message.includes('No authenticated Microsoft account')) {
      return {
        success: false,
        error: 'Please connect to Microsoft Outlook first to use Teams integration.',
        requiresAuth: true
      };
    }
    
    return {
      success: false,
      error: `Failed to create Teams meeting: ${error.message}`
    };
  }
}

/**
 * Format meeting data for Teams calendar event
 */
function formatMeetingForTeams(meetingData) {
  const { title, date, time, duration = 60, details = '', attendees = [] } = meetingData;
  
  // ✅ Ensure attendees array is valid and email format is correct
  let validAttendees = [];
  if (Array.isArray(attendees)) {
    validAttendees = attendees.filter(email => {
      if (typeof email !== 'string' || !email.includes('@') || email.length < 5) {
        console.warn(`Invalid attendee email skipped: ${email}`);
        return false;
      }
      return true;
    });
    
    if (validAttendees.length !== attendees.length) {
      console.warn(`${attendees.length - validAttendees.length} invalid attendee emails were filtered out`);
    }
  } else if (attendees) {
    console.warn('Attendees must be an array, using empty array instead');
  }
  
  // Parse date and time
  const startDateTime = time 
    ? new Date(`${date}T${time}:00`)
    : new Date(`${date}T09:00:00`);
    
  const endDateTime = new Date(startDateTime.getTime() + (duration * 60 * 1000));
  
  // ✅ Validate dates are valid
  if (isNaN(startDateTime.getTime()) || isNaN(endDateTime.getTime())) {
    throw new Error('Invalid date/time format in meeting data');
  }
  
  return {
    subject: title || 'Teams Meeting',
    body: {
      contentType: 'text',
      content: details || 'Microsoft Teams meeting'
    },
    start: {
      dateTime: startDateTime.toISOString(),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    },
    end: {
      dateTime: endDateTime.toISOString(),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
    },
    attendees: validAttendees.map(email => ({
      emailAddress: {
        address: email.trim(),
        name: email.trim()
      },
      type: 'required'
    })),
    isOnlineMeeting: true,
    onlineMeetingProvider: 'teamsForBusiness'
  };
}

/**
 * Create calendar event with Teams meeting using Microsoft Graph API
 */
async function createCalendarEventWithTeams(accessToken, eventData) {
  try {
    // ✅ Add try/catch around Microsoft Graph API fetches
    let response;
    try {
      response = await fetch(
        'https://graph.microsoft.com/v1.0/me/events',
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(eventData)
        }
      );
    } catch (fetchError) {
      console.error('Network error during Teams event creation:', fetchError);
      throw new Error(`Network error: ${fetchError.message || 'Failed to reach Microsoft Graph API'}`);
    }

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch (parseError) {
        console.error('Error parsing Teams API error response:', parseError);
        throw new Error(`Teams meeting creation failed: ${response.status} ${response.statusText}`);
      }
      
      console.error('Teams meeting creation error:', errorData);
      throw new Error(`Failed to create Teams meeting: ${response.status} - ${errorData.error?.message || response.statusText}`);
    }

    let calendarEvent;
    try {
      calendarEvent = await response.json();
    } catch (parseError) {
      console.error('Error parsing Teams event creation response:', parseError);
      throw new Error('Invalid JSON response from Microsoft Graph API during Teams event creation');
    }
    
    console.log('Teams meeting created successfully:', calendarEvent);
    
    return calendarEvent;
  } catch (error) {
    console.error('Error in createCalendarEventWithTeams:', error);
    throw error;
  }
}

/**
 * Check if Teams integration is available
 */
export function isTeamsAvailable() {
  try {
    const accounts = msalInstance.getAllAccounts();
    return accounts.length > 0;
  } catch (error) {
    console.error('Error checking Teams availability:', error);
    return false;
  }
}

/**
 * Check if user is connected to Microsoft account
 */
export function isTeamsConnected() {
  return isTeamsAvailable();
}

/**
 * Get Teams connection status info
 */
export function getTeamsConnectionInfo() {
  try {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      return {
        connected: false,
        message: 'Connect to Microsoft Outlook to enable Teams meetings'
      };
    }

    const account = accounts[0];
    return {
      connected: true,
      userInfo: {
        email: account.username,
        name: account.name || account.username
      },
      message: 'Teams integration ready'
    };
  } catch (error) {
    return {
      connected: false,
      message: 'Error checking Teams connection',
      error: error.message
    };
  }
}

/**
 * Test Teams meeting creation capability
 */
export async function testTeamsConnection() {
  try {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      return {
        success: false,
        message: 'No Microsoft account connected. Please connect to Outlook first.'
      };
    }

    // ✅ Add try/catch around Microsoft Graph API fetches  
    let response;
    try {
      // Try to get a token with Teams permissions
      const tokenRequest = {
        account: accounts[0],
        scopes: teamsScopes,
        forceRefresh: false
      };

      const tokenResponse = await msalInstance.acquireTokenSilent(tokenRequest);
      
      // Test access to the Graph API
      response = await fetch(
        'https://graph.microsoft.com/v1.0/me/onlineMeetings',
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${tokenResponse.accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );
    } catch (apiError) {
      console.error('Teams API access error:', apiError);
      return {
        success: false,
        message: `Teams API access failed: ${apiError.message || 'Unknown API error'}`
      };
    }

    if (response.ok) {
      return {
        success: true,
        message: 'Teams integration is working! You can create Teams meetings.'
      };
    } else {
      return {
        success: false,
        message: `Teams API access issue: ${response.status} ${response.statusText}`
      };
    }
  } catch (error) {
    console.error('Teams connection test error:', error);
    return {
      success: false,
      message: `Teams connection test failed: ${error.message}`
    };
  }
}

export default {
  createTeamsMeeting,
  isTeamsAvailable,
  isTeamsConnected,
  getTeamsConnectionInfo,
  testTeamsConnection
};
